import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { 
  Volume2, 
  VolumeX, 
  Loader2, 
  Pause, 
  Play,
  Settings2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Available voices
const VOICES = [
  { id: 'nova', name: 'Nova', emoji: '👩', desc: 'Chaleureuse' },
  { id: 'coral', name: 'Coral', emoji: '👩', desc: 'Naturelle' },
  { id: 'shimmer', name: 'Shimmer', emoji: '👩', desc: 'Claire' },
  { id: 'alloy', name: 'Alloy', emoji: '🤖', desc: 'Neutre' },
  { id: 'echo', name: 'Echo', emoji: '👨', desc: 'Douce' },
  { id: 'onyx', name: 'Onyx', emoji: '👨', desc: 'Profonde' },
];

/**
 * TextToSpeech Component
 * Reads text aloud using OpenAI TTS
 * 
 * Props:
 * - text: string - The text to read
 * - language: 'fr' | 'en' - UI language
 * - variant: 'button' | 'icon' | 'fab' - Button style
 * - size: 'sm' | 'md' | 'lg' - Button size
 * - className: string - Additional CSS classes
 * - autoPlay: boolean - Auto-play when text changes
 * - showVoiceSelector: boolean - Show voice selection dropdown
 */
const TextToSpeech = ({ 
  text, 
  language = 'fr',
  variant = 'button',
  size = 'md',
  className = '',
  autoPlay = false,
  showVoiceSelector = true
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [audioUrl, setAudioUrl] = useState(null);
  
  const audioRef = useRef(null);

  // Generate and play speech
  const speak = async () => {
    if (!text || !text.trim()) {
      toast.error(language === 'fr' ? 'Aucun texte à lire' : 'No text to read');
      return;
    }

    // If already loaded, just play
    if (audioUrl && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
      setIsPaused(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/voice/speak`, {
        text: text,
        voice: selectedVoice,
        model: 'tts-1',
        speed: 1.0
      });

      if (response.data.success) {
        // Convert base64 to audio blob
        const audioData = atob(response.data.audio_base64);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(audioBlob);
        
        setAudioUrl(url);
        
        // Play audio
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' 
        ? 'Erreur de synthèse vocale' 
        : 'Text-to-speech error'
      ));
    } finally {
      setIsLoading(false);
    }
  };

  // Pause/Resume
  const togglePause = () => {
    if (audioRef.current) {
      if (isPaused) {
        audioRef.current.play();
        setIsPaused(false);
      } else {
        audioRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  // Stop
  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPaused(false);
    }
  };

  // Handle audio end
  const handleAudioEnd = () => {
    setIsPlaying(false);
    setIsPaused(false);
  };

  // Change voice (resets audio)
  const changeVoice = (voiceId) => {
    setSelectedVoice(voiceId);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    stop();
  };

  // Get button size classes
  const sizeClasses = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4',
    lg: 'h-12 px-6 text-lg'
  };

  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12'
  };

  const iconSize = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Render based on variant
  const renderButton = () => {
    if (variant === 'icon') {
      return (
        <Button
          onClick={isPlaying && !isPaused ? togglePause : speak}
          disabled={isLoading || !text}
          size="icon"
          variant="outline"
          className={`rounded-full ${iconSizeClasses[size]} ${className}`}
          data-testid="tts-icon-btn"
        >
          {isLoading ? (
            <Loader2 className={`${iconSize[size]} animate-spin`} />
          ) : isPlaying && !isPaused ? (
            <Pause className={iconSize[size]} />
          ) : (
            <Volume2 className={iconSize[size]} />
          )}
        </Button>
      );
    }

    if (variant === 'fab') {
      return (
        <Button
          onClick={isPlaying && !isPaused ? togglePause : speak}
          disabled={isLoading || !text}
          className={`fixed bottom-24 right-4 rounded-full shadow-lg z-50 ${iconSizeClasses.lg} bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white ${className}`}
          data-testid="tts-fab-btn"
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : isPlaying && !isPaused ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Volume2 className="w-6 h-6" />
          )}
        </Button>
      );
    }

    // Default button variant
    return (
      <Button
        onClick={isPlaying && !isPaused ? togglePause : speak}
        disabled={isLoading || !text}
        className={`${sizeClasses[size]} bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white ${className}`}
        data-testid="tts-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className={`${iconSize[size]} mr-2 animate-spin`} />
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </>
        ) : isPlaying && !isPaused ? (
          <>
            <Pause className={`${iconSize[size]} mr-2`} />
            {language === 'fr' ? 'Pause' : 'Pause'}
          </>
        ) : (
          <>
            <Volume2 className={`${iconSize[size]} mr-2`} />
            {language === 'fr' ? 'Écouter' : 'Listen'}
          </>
        )}
      </Button>
    );
  };

  return (
    <div className="inline-flex items-center gap-2">
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={handleAudioEnd}
        className="hidden"
      />

      {/* Main button */}
      {renderButton()}

      {/* Stop button (only when playing) */}
      {isPlaying && (
        <Button
          onClick={stop}
          size="icon"
          variant="outline"
          className={`rounded-full ${iconSizeClasses[size]}`}
          data-testid="tts-stop-btn"
        >
          <VolumeX className={iconSize[size]} />
        </Button>
      )}

      {/* Voice selector */}
      {showVoiceSelector && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={`rounded-full ${iconSizeClasses[size]}`}
              data-testid="tts-voice-selector"
            >
              <Settings2 className={iconSize[size]} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {language === 'fr' ? 'Choisir une voix' : 'Choose a voice'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {VOICES.map(voice => (
              <DropdownMenuItem
                key={voice.id}
                onClick={() => changeVoice(voice.id)}
                className={selectedVoice === voice.id ? 'bg-purple-50 text-purple-700' : ''}
              >
                <span className="mr-2">{voice.emoji}</span>
                <span className="font-medium">{voice.name}</span>
                <span className="ml-2 text-xs text-gray-500">({voice.desc})</span>
                {selectedVoice === voice.id && (
                  <span className="ml-auto text-purple-600">✓</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default TextToSpeech;
